import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { getPharmacyDashboardData } from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import DispenseConfirmationModal from "./components/DispenseConfirmationModal";
import PharmacyDetailsView from "./components/PharmacyDetailsView";
import TransferFacilityModal from "./components/TransferFacilityModal";
import { mockPharmacyData } from "./mockPharmacyDashboardData";

const initialFilters = {
  source: "All",
  status: "All",
  facility: "All Facilities",
  veterinarian: "All Vets",
  dateRange: "Today",
  searchQuery: "",
};

// Toggle button component matching NewCalendar's CalToggleBtn design
const CalToggleBtn = ({ active, onClick, children }) => (
  <Typography
    component="div"
    className={`font-bold fs14 cursor flex-center ${
      active ? "blue-back-con2" : "day-type-box2 black"
    }`}
    onClick={onClick}
    style={{ whiteSpace: "nowrap", padding: "6px 16px", minHeight: "32px" }}
  >
    {children}
  </Typography>
);

const PharmacyDashboardNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all"); // all, dispense, transfer
  const [layoutView, setLayoutView] = useState("grid"); // grid, list
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({ ...initialFilters });
  const [appliedFilters, setAppliedFilters] = useState({ ...initialFilters });
  const [selectedRecord, setSelectedRecord] = useState(null);
  // Modal selection state
  const [activeModal, setActiveModal] = useState(null); // "dispense" | "transfer" | null
  const [activeRecord, setActiveRecord] = useState(null);
  const { pharmacyDashboard } = useSelector((state) => state?.clinic);

  useEffect(() => {
    dispatch(getPharmacyDashboardData({ type: "Outpatient" }));
  }, []);

  const handleOpenDispense = (record) => {
    setActiveModal("dispense");
    setActiveRecord(record);
  };

  const handleOpenTransfer = (record) => {
    setActiveModal("transfer");
    setActiveRecord(record);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setActiveRecord(null);
  };

  const handleOpenDetails = (appointment) => {
    navigate("/pharmacy-pet-details", {
      state: { appointment: { appoinment: appointment?.appointment } },
    });
    // setSelectedRecord(record);
  };

  const handleCloseDetails = () => {
    setSelectedRecord(null);
  };

  // Filter application matching states
  const filteredData = useMemo(() => {
    return mockPharmacyData.filter((item) => {
      // Tab filter
      if (activeTab === "dispense") {
        if (
          item.dispenseCount === 0 &&
          !item.actionButtons.includes("Dispense")
        )
          return false;
      } else if (activeTab === "transfer") {
        if (
          item.transferCount === 0 &&
          !item.actionButtons.includes("Transfer")
        )
          return false;
      }

      // Source filter
      if (appliedFilters.source !== "All") {
        if (!item.sources.includes(appliedFilters.source)) return false;
      }

      // Status filter
      if (appliedFilters.status !== "All") {
        if (item.status !== appliedFilters.status) return false;
      }

      // Facility/Room filter
      if (appliedFilters.facility !== "All Facilities") {
        if (item.facility !== appliedFilters.facility) return false;
      }

      // Veterinarian filter
      if (appliedFilters.veterinarian !== "All Vets") {
        if (item.veterinarian !== appliedFilters.veterinarian) return false;
      }

      // Date Range filter
      const todayStr = "2026-07-17";
      const startOfWeekStr = "2026-07-13"; // Monday of the week
      const endOfWeekStr = "2026-07-19"; // Sunday of the week
      if (appliedFilters.dateRange === "Today") {
        if (item.date !== todayStr) return false;
      } else if (appliedFilters.dateRange === "This Week") {
        if (item.date < startOfWeekStr || item.date > endOfWeekStr)
          return false;
      } else if (appliedFilters.dateRange === "Custom") {
        if (item.date > startOfWeekStr) return false; // placeholder for older dates
      }

      // Search Query
      if (appliedFilters.searchQuery?.trim()) {
        const q = appliedFilters.searchQuery.trim().toLowerCase();
        const patientMatch = item.patientName?.toLowerCase().includes(q);
        const ownerMatch = item.ownerName?.toLowerCase().includes(q);
        if (!patientMatch && !ownerMatch) return false;
      }

      return true;
    });
  }, [activeTab, appliedFilters]);

  // Tab Badge counts calculated dynamically
  const counts = useMemo(() => {
    const baseFiltered = mockPharmacyData.filter((item) => {
      if (appliedFilters.source !== "All") {
        if (!item.sources.includes(appliedFilters.source)) return false;
      }
      if (appliedFilters.status !== "All") {
        if (item.status !== appliedFilters.status) return false;
      }
      if (appliedFilters.facility !== "All Facilities") {
        if (item.facility !== appliedFilters.facility) return false;
      }
      if (appliedFilters.veterinarian !== "All Vets") {
        if (item.veterinarian !== appliedFilters.veterinarian) return false;
      }
      const todayStr = "2026-07-17";
      const startOfWeekStr = "2026-07-13";
      const endOfWeekStr = "2026-07-19";
      if (appliedFilters.dateRange === "Today") {
        if (item.date !== todayStr) return false;
      } else if (appliedFilters.dateRange === "This Week") {
        if (item.date < startOfWeekStr || item.date > endOfWeekStr)
          return false;
      } else if (appliedFilters.dateRange === "Custom") {
        if (item.date > startOfWeekStr) return false;
      }
      if (appliedFilters.searchQuery?.trim()) {
        const q = appliedFilters.searchQuery.trim().toLowerCase();
        const patientMatch = item.patientName?.toLowerCase().includes(q);
        const ownerMatch = item.ownerName?.toLowerCase().includes(q);
        if (!patientMatch && !ownerMatch) return false;
      }
      return true;
    });

    const all = baseFiltered.length;
    const dispense = baseFiltered.filter(
      (item) =>
        item.dispenseCount > 0 || item.actionButtons.includes("Dispense"),
    ).length;
    const transfer = baseFiltered.filter(
      (item) =>
        item.transferCount > 0 || item.actionButtons.includes("Transfer"),
    ).length;

    return { all, dispense, transfer };
  }, [appliedFilters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    setAppliedFilters({ ...filterValues });
    setFilterOpen(false);
  };

  const handleFilterReset = () => {
    setFilterValues({ ...initialFilters });
    setAppliedFilters({ ...initialFilters });
    setFilterOpen(false);
  };

  // Badge Render Helpers
  const renderSourceBadge = (src) => {
    let style = { bg: "#e8f0fe", color: "#1a73e8" }; // Prescription
    if (src === "Administration") {
      style = { bg: "#f3e5f5", color: "#7b1fa2" }; // Administration
    } else if (src === "Refill") {
      style = { bg: "#e6f4ea", color: "#137333" }; // Refill
    }

    return (
      <span
        key={src}
        style={{
          backgroundColor: style.bg,
          color: style.color,
          fontSize: "11px",
          fontWeight: "bold",
          padding: "3px 8px",
          borderRadius: "4px",
          textTransform: "uppercase",
          fontFamily: "Montserrat",
        }}
      >
        {src}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    let style = { bg: "#fff8e1", color: "#b78103" }; // Scheduled
    if (status === "RESTRICTED") {
      style = { bg: "#fce8e6", color: "#c5221f" }; // Restricted
    }

    return (
      <span
        key={status}
        style={{
          backgroundColor: style.bg,
          color: style.color,
          fontSize: "10px",
          fontWeight: "bold",
          padding: "2px 6px",
          borderRadius: "4px",
          textTransform: "uppercase",
          fontFamily: "Montserrat",
        }}
      >
        {status}
      </span>
    );
  };

  if (selectedRecord) {
    return (
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          minHeight: "90vh",
          pb: 5,
          overflowY: "auto",
          height: "100%",
          padding: "24px",
        }}
      >
        <PharmacyDetailsView
          record={selectedRecord}
          onBack={handleCloseDetails}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        minHeight: "90vh",
        pb: 5,
        overflowY: "auto",
        height: "100%",
      }}
    >
      {/* Top Banner Tab & Actions Row matching NewCalendar toolbar header styles */}
      <div className="ph15 mh10 mt10" style={{ paddingTop: "10px" }}>
        <div className="back-white br8brblack p10 mv5">
          <div
            className="flex-row-between flex-row-wrap"
            style={{ gap: "10px" }}
          >
            {/* Left Side: Tabs pills with badge counts */}
            <div className="flex-row-ali-cen" style={{ gap: "8px" }}>
              <CalToggleBtn
                active={activeTab === "all"}
                onClick={() => setActiveTab("all")}
              >
                <div className="flex-row-ali-cen" style={{ gap: "6px" }}>
                  <span>All</span>
                  <span
                    style={{
                      backgroundColor:
                        activeTab === "all" ? "#1a73e8" : "#f1f3f4",
                      color: activeTab === "all" ? "#fff" : "#5f6368",
                      fontSize: "11px",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {pharmacyDashboard?.length}
                  </span>
                </div>
              </CalToggleBtn>

              {/* <CalToggleBtn
                active={activeTab === "dispense"}
                onClick={() => setActiveTab("dispense")}
              >
                <div className="flex-row-ali-cen" style={{ gap: "6px" }}>
                  <span>Dispense</span>
                  <span
                    style={{
                      backgroundColor: "#ea4335",
                      color: "#fff",
                      fontSize: "11px",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {counts.dispense}
                  </span>
                </div>
              </CalToggleBtn>

              <CalToggleBtn
                active={activeTab === "transfer"}
                onClick={() => setActiveTab("transfer")}
              >
                <div className="flex-row-ali-cen" style={{ gap: "6px" }}>
                  <span>Transfer</span>
                  <span
                    style={{
                      backgroundColor:
                        activeTab === "transfer" ? "#1a73e8" : "#5f6368",
                      color: "#fff",
                      fontSize: "11px",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {counts.transfer}
                  </span>
                </div>
              </CalToggleBtn> */}
            </div>

            {/* Right Side: Filter and Switcher with NewCalendar exact styles */}
            {/* <div className="flex-end">
              <div className="flex-row-ali-cen">
                <Typography
                  className="cursor flex-row-ali-cen mh10 day-type-box2 pv4ph10"
                  onClick={() => setFilterOpen(true)}
                  sx={{ height: "32px" }}
                >
                  <TuneIcon
                    sx={{
                      width: 18,
                      height: 18,
                      color: AppColors.appPrimary,
                    }}
                  />
                  <Typography
                    className="font-medium fs12 blue-color ml10"
                    sx={{ fontFamily: "Montserrat" }}
                  >
                    Filter
                  </Typography>
                </Typography>

                <div className="flex-row-ali-cen">
                  <div
                    className={`cursor cal-tab-icon day-type-box ${
                      layoutView === "grid" ? "blue-back-con" : ""
                    }`}
                    onClick={() => setLayoutView("grid")}
                    style={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  >
                    <DashboardIcon
                      sx={{
                        height: 20,
                        width: 20,
                        color:
                          layoutView === "grid"
                            ? AppColors.white
                            : AppColors.gray,
                      }}
                    />
                  </div>
                  <div
                    className={`cursor cal-tab-icon day-type-box ${
                      layoutView === "list" ? "blue-back-con" : ""
                    }`}
                    onClick={() => setLayoutView("list")}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    <TableRowsIcon
                      sx={{
                        height: 20,
                        width: 20,
                        color:
                          layoutView === "list"
                            ? AppColors.white
                            : AppColors.gray,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      <div className="scroll-80vh">
        {/* Main Content Body */}
        <Box sx={{ padding: "24px" }}>
          {pharmacyDashboard?.length === 0 ? (
            // Empty State Component
            <Paper
              elevation={0}
              className="br15"
              sx={{
                padding: "48px",
                textAlign: "center",
                backgroundColor: "#fff",
                border: "1px solid #00000020",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: AppColors.gray,
                  mb: 1,
                  fontFamily: "Montserrat",
                }}
              >
                No Records Found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: AppColors.gray2, mb: 3, fontFamily: "Montserrat" }}
              >
                There are no pharmacy records matching your current filter
                criteria.
              </Typography>
              <CustomButton
                text="Clear Filters"
                smallBtn
                onClick={handleFilterReset}
              />
            </Paper>
          ) : layoutView === "grid" ? (
            // Grid Layout
            <Grid container spacing={3}>
              {pharmacyDashboard?.map((item) => {
                const apnt = item?.appointment;
                return (
                  <Grid item xs={12} sm={6} md={4} key={apnt?.appointmentId}>
                    <Paper
                      elevation={0}
                      className="br15"
                      onClick={() => handleOpenDetails(item)}
                      sx={{
                        padding: "20px",
                        border: "1px solid #00000020",
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <Box>
                        {/* Source badging & Vet */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          {/* <Box display="flex" gap={0.5}>
                            {item.sources.map((src) => renderSourceBadge(src))}
                          </Box> */}
                          <Typography
                            variant="body2"
                            sx={{
                              color: AppColors.appPrimary,
                              fontWeight: 600,
                              fontSize: "12px",
                              fontFamily: "Montserrat",
                            }}
                          >
                            {apnt?.doctorName}
                          </Typography>
                        </Box>

                        {/* Date / Time & Facility */}
                        <Box mb={2}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              fontSize: "15px",
                              color: AppColors.black,
                              fontFamily: "Montserrat",
                            }}
                          >
                            {moment(apnt?.appointmentdateTime).format(
                              "DD MMM YYYY, HH:mm",
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: AppColors.gray,
                              fontSize: "12px",
                              fontFamily: "Montserrat",
                            }}
                          >
                            {item.facilityNotes ?? item.facility ?? "—"}
                          </Typography>
                        </Box>

                        {/* Patient Information Panel */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            padding: "12px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: "#fff2cc",
                              borderRadius: "8px",
                              width: "48px",
                              height: "48px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "24px",
                            }}
                          >
                            🐶
                          </Box>
                          <Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                  color: AppColors.black,
                                  fontFamily: "Montserrat",
                                }}
                              >
                                {apnt?.petName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "medium",
                                  textTransform: "capitalize",
                                  color:
                                    apnt?.gender === "male"
                                      ? "#1a73e8"
                                      : "#e06666",
                                  fontFamily: "Montserrat",
                                }}
                              >
                                ({apnt?.gender})
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                color: AppColors.gray,
                                fontSize: "12px",
                                fontFamily: "Montserrat",
                              }}
                            >
                              {apnt?.breed}
                            </Typography>
                            <Typography
                              sx={{
                                color: AppColors.gray,
                                fontSize: "12px",
                                fontWeight: "medium",
                                fontFamily: "Montserrat",
                              }}
                            >
                              {apnt?.userName}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Gray Strip Status Summary */}
                      <Box>
                        <Box
                          sx={{
                            backgroundColor: "#f8f9fa",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: AppColors.gray3,
                              fontFamily: "Montserrat",
                            }}
                          >
                            {item.itemsSummary}
                          </Typography>
                          {/* <Box display="flex" gap={0.5}>
                            {item.statusBadges.map((badge) =>
                              renderStatusBadge(badge),
                            )}
                          </Box> */}
                        </Box>

                        {/* Bottom Action Row */}
                        <Box display="flex" alignItems="center" gap={1}>
                          {/* {item?.actionButtons?.includes("Dispense") && ( */}
                          {/* <Box sx={{ flex: 1 }}>
                            <CustomButton
                              text="Dispense"
                              smallBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDispense(item);
                              }}
                            />
                          </Box> */}
                          {/* )} */}
                          {/* {item?.actionButtons.includes("Transfer") && ( */}
                          {/* <Box sx={{ flex: 1 }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenTransfer(item);
                              }}
                              style={{
                                backgroundColor: "#7b1fa2",
                                color: "#fff",
                                border: "none",
                                borderRadius: "5px",
                                height: "30px",
                                width: "100%",
                                fontSize: "14px",
                                fontWeight: "600",
                                fontFamily: "Montserrat",
                                cursor: "pointer",
                                textTransform: "capitalize",
                                boxShadow: "none",
                              }}
                            >
                              Transfer
                            </button>
                          </Box> */}
                          {/* )} */}
                          <Box sx={{ flex: 1 }}>
                            <CustomButton
                              text="View Details"
                              smallBtn
                              whiteBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDetails(item);
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            // List View (Table Layout)
            <TableContainer
              component={Paper}
              elevation={0}
              className="br15"
              sx={{ border: "1px solid #00000020" }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Source
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Date / Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Facility
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Vet
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Patient
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Owner
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Items
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Montserrat",
                        color: AppColors.gray3,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => handleOpenDetails(item)}
                      sx={{
                        cursor: "pointer",
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      {/* Source badging */}
                      <TableCell>
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                          alignItems="flex-start"
                        >
                          <Box display="flex" gap={0.5}>
                            {item.sources.map((src) => renderSourceBadge(src))}
                          </Box>
                          <Box display="flex" gap={0.5} mt={0.5}>
                            {item.statusBadges.map((badge) =>
                              renderStatusBadge(badge),
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      {/* Date / Time */}
                      <TableCell
                        sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
                      >
                        {item.date === "2026-07-17"
                          ? `Today, ${item.time}`
                          : `${moment(item.date).format("DD MMM YYYY")}, ${item.time}`}
                      </TableCell>
                      {/* Facility */}
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        {item.facility ?? "—"}
                      </TableCell>
                      {/* Vet */}
                      <TableCell
                        sx={{
                          fontFamily: "Montserrat",
                          fontWeight: 600,
                          color: AppColors.appPrimary,
                        }}
                      >
                        {item.veterinarian}
                      </TableCell>
                      {/* Patient & Breed */}
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "14px",
                              fontFamily: "Montserrat",
                            }}
                          >
                            {item.patientName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: AppColors.gray3,
                              fontFamily: "Montserrat",
                            }}
                          >
                            ({item.breed})
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Owner */}
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        {item.ownerName}
                      </TableCell>
                      {/* Items */}
                      <TableCell
                        sx={{ fontFamily: "Montserrat", fontWeight: "bold" }}
                      >
                        {item.dispenseCount > 0 && item.transferCount > 0
                          ? `${item.dispenseCount} + ${item.transferCount}`
                          : item.dispenseCount > 0
                            ? `${item.dispenseCount}`
                            : `${item.transferCount}`}
                      </TableCell>
                      {/* Actions */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {item.actionButtons.includes("Dispense") && (
                            <CustomButton
                              text="Dispense"
                              smallBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDispense(item);
                              }}
                            />
                          )}
                          {item.actionButtons.includes("Transfer") && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenTransfer(item);
                              }}
                              style={{
                                backgroundColor: "#7b1fa2",
                                color: "#fff",
                                border: "none",
                                borderRadius: "5px",
                                height: "30px",
                                padding: "0 14px",
                                fontSize: "14px",
                                fontWeight: "600",
                                fontFamily: "Montserrat",
                                cursor: "pointer",
                                textTransform: "capitalize",
                                boxShadow: "none",
                              }}
                            >
                              Transfer
                            </button>
                          )}
                          <CustomButton
                            text="View"
                            smallBtn
                            whiteBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDetails(item);
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </div>
      {/* Filter Drawer Component (CustomModal rightModal) */}
      <CustomModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        header="Filter"
        rightModal
        modalWidth="30"
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ padding: "0 10px" }}
        >
          {/* Source Dropdown Select */}
          <Box>
            <CustomSelect
              label="Source"
              name="source"
              list={[
                { name: "All", value: "All" },
                { name: "Prescription", value: "Prescription" },
                { name: "Refill", value: "Refill" },
                { name: "Administration", value: "Administration" },
              ]}
              value={filterValues.source}
              handleChange={handleFilterChange}
              select
            />
          </Box>

          {/* Status Dropdown Select */}
          <Box>
            <CustomSelect
              label="Status"
              name="status"
              list={[
                { name: "All", value: "All" },
                { name: "Pending", value: "Pending" },
                { name: "Completed", value: "Completed" },
              ]}
              value={filterValues.status}
              handleChange={handleFilterChange}
              select
            />
          </Box>

          {/* Facility / Room Dropdown Select */}
          <Box>
            <CustomSelect
              label="Facility / Room"
              name="facility"
              list={[
                { name: "All Facilities", value: "All Facilities" },
                { name: "Consult Room 1", value: "Consult Room 1" },
                { name: "Consult Room 2", value: "Consult Room 2" },
                { name: "Surgery", value: "Surgery" },
                { name: "OT", value: "OT" },
              ]}
              value={filterValues.facility}
              handleChange={handleFilterChange}
              select
            />
          </Box>

          {/* Veterinarian Dropdown Select */}
          <Box>
            <CustomSelect
              label="Veterinarian"
              name="veterinarian"
              list={[
                { name: "All Vets", value: "All Vets" },
                { name: "Dr. Vivek", value: "Dr. Vivek" },
                { name: "Dr. Supervet", value: "Dr. Supervet" },
              ]}
              value={filterValues.veterinarian}
              handleChange={handleFilterChange}
              select
            />
          </Box>

          {/* Date Range Dropdown Select */}
          <Box>
            <CustomSelect
              label="Date Range"
              name="dateRange"
              list={[
                { name: "Today", value: "Today" },
                { name: "This Week", value: "This Week" },
                { name: "Custom (Placeholder)", value: "Custom" },
              ]}
              value={filterValues.dateRange}
              handleChange={handleFilterChange}
              select
            />
          </Box>

          {/* Patient / Owner Search Bar */}
          <Box>
            <CustomTextField
              fullWidth
              label="Patient / Owner"
              placeholder="Search by name..."
              name="searchQuery"
              value={filterValues.searchQuery}
              handleChange={handleFilterChange}
            />
          </Box>

          {/* Reset / Apply Buttons */}
          <Box display="flex" justifyContent="center" gap={2} mt={2} mb={2}>
            <CustomButton
              text="Reset"
              smallBtn
              redBtn
              onClick={handleFilterReset}
            />
            <CustomButton text="Apply" smallBtn onClick={handleFilterApply} />
          </Box>
        </Box>
      </CustomModal>

      {/* Confirmation and Transfer Modals */}
      <DispenseConfirmationModal
        open={activeModal === "dispense"}
        onClose={handleCloseModal}
        record={activeRecord}
        onConfirm={(record) => {
          console.log("Confirmed dispense for record:", record.id);
        }}
      />

      <TransferFacilityModal
        open={activeModal === "transfer"}
        onClose={handleCloseModal}
        record={activeRecord}
        onTransfer={(record, destination) => {
          console.log(
            "Transferred record:",
            record.id,
            "to destination:",
            destination,
          );
        }}
      />
    </Box>
  );
};

export default PharmacyDashboardNew;
